import { Environment, KeyboardControls, useProgress } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { Physics } from "@react-three/rapier";
// import { useControls } from "leva";
// import { Perf } from "r3f-perf";
import { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import "../GamePage.scss";
import { shuffleArray } from "../../../utils/shuffleArray";
import Ecctrl from "./Ecctrl";
import EntityAI from "./EntityAI";
import Player from "./Actors/Player";
import Ground from "./Environment/Ground";
import Lights from "./Environment/Lights";
import Sky from "./Environment/Sky";
import Trees from "./Environment/Trees";
import Spell from "./Magic/Spell";
import SpellHit from "./Magic/SpellHit";
import MemoryBar from "./HUD/MemoryBar";
import Menu from "./Screens/Menu";
import Minimap from "./HUD/Minimap";
import Quiz from "./Screens/Quiz";
// import StaminaBar from "./stats/StaminaBar";
import Settings from "./Screens/Settings";
import { useGameStore } from "../stores/gameStore";
import { Scroll, ScrollStates } from "./Assets/Scroll";
import { User as UserModel } from "../../../models/user";
import * as UserApi from "../../../network/user_api";
import { GameStates } from "../constants/GameStates";
import { MusicPaths } from "../constants/MusicPaths";
import { SplashScreen } from "./Screens/SplashScreen";
import { useTranslation } from "react-i18next";
import { Pouch, PouchStates } from "./Assets/Pouch";

const GameCanvas = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserModel>();
  const [userLoading, setUserLoading] = useState(true);
  const [showUserLoadingError, setShowUserLoadingError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [splashStart, setSplashStart] = useState<number | null>(null); // NEW

  const { progress } = useProgress();

  useEffect(() => {
    if (splashStart === null && progress < 100) {
      setSplashStart(Date.now());
    }

    if (progress === 100 && splashStart !== null) {
      const elapsed = Date.now() - splashStart;
      const remaining = Math.max(0, 5000 - elapsed);

      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, remaining);

      return () => clearTimeout(timeout);
    }
  }, [progress, splashStart]);

  useEffect(() => {
    async function loadUser() {
      try {
        setShowUserLoadingError(false);
        setUserLoading(true);
        const user = await UserApi.fetchUser();
        setUser(user);
      } catch (error) {
        console.error(error);
        alert(error);
        setShowUserLoadingError(true);
      } finally {
        setUserLoading(false);
      }
    }
    loadUser();
  }, []);

  const {
    gameState,
    isMusicOn,
    setIsFullscreen,
    entity,
    scrollState,
    pouchState,
  } = useGameStore((state: any) => ({
    gameState: state.gameState,
    isMusicOn: state.isMusicOn,
    setIsFullscreen: state.setIsFullscreen,
    entity: state.entity,
    scrollState: state.scrollState,
    pouchState: state.pouchState,
  }));
  const keyboardMap = [
    { name: "forward", keys: ["ArrowUp", "KeyW"] },
    { name: "backward", keys: ["ArrowDown", "KeyS"] },
    { name: "leftward", keys: ["ArrowLeft", "KeyA"] },
    { name: "rightward", keys: ["ArrowRight", "KeyD"] },
    { name: "jump", keys: ["Space"] },
    { name: "run", keys: ["Shift"] },
  ];

  const handle = useFullScreenHandle();
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [spells, setSpells] = useState<object[]>([]);
  const [hits, setHits] = useState<any[]>([]);

  const onCastSpell = (newSpell: object) => {
    setSpells((spells) => [...spells, newSpell]);
  };

  const onHit = (spellId: string, position: any) => {
    setSpells((spells) =>
      spells.filter((spell) => (spell as any).id !== spellId)
    );
    setHits((hits) => [...hits, { id: spellId, position }]);
  };

  const onHitEnded = (hitId: any) => {
    setHits((hits) => hits.filter((hit) => hit.id !== hitId));
  };

  const playMusic = useCallback((paths: string[], currentIndex: number = 0) => {
    const music = new Audio(`/sounds/${paths[currentIndex]}.wav`);
    music.addEventListener("ended", () => {
      const nextIndex = (currentIndex + 1) % paths.length;
      playMusic(paths, nextIndex);
    });
    music.volume = 0.1;
    music.play();
    return music;
  }, []);

  useEffect(() => {
    let musicPlayer: HTMLAudioElement | null = null;
    const ambientMusics = [
      MusicPaths.AMBIENT1,
      MusicPaths.AMBIENT2,
      MusicPaths.AMBIENT3,
      MusicPaths.AMBIENT4,
    ];
    const actionMusics = [MusicPaths.ACTION1];

    const stopMusic = () => {
      if (musicPlayer) {
        musicPlayer.pause();
        musicPlayer = null;
      }
    };

    const playAmbientMusic = () => {
      const shuffledMusics = shuffleArray(ambientMusics);
      musicPlayer = playMusic(shuffledMusics);
    };

    const playActionMusic = () => {
      musicPlayer = playMusic(actionMusics);
    };

    const manageMusic = () => {
      if (isMusicOn) {
        if (
          gameState === GameStates.WON ||
          gameState === GameStates.GAME_OVER ||
          gameState === GameStates.MENU
        ) {
          stopMusic();
        } else if (entity.canSeeTarget) {
          stopMusic();
          playActionMusic();
        } else {
          stopMusic();
          playAmbientMusic();
        }
      } else {
        stopMusic();
      }
    };

    manageMusic();

    return () => {
      stopMusic();
    };
  }, [isMusicOn, gameState, entity.canSeeTarget, playMusic]);

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (
        (e.code === "Space" ||
          e.code === "ArrowLeft" ||
          e.code === "ArrowRight" ||
          e.code === "ArrowUp" ||
          e.code === "ArrowDown") &&
        e.target === document.body
      ) {
        e.preventDefault();
      }
      if (e.code === "Tab" && gameState === GameStates.GAME) {
        e.preventDefault();
        setShowSettings((prev) => !prev);
      }
    };

    const handleClick = () => {
      if (showSettings) {
        setShowSettings(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("click", handleClick);
    };
  }, [gameState, showSettings]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [setIsFullscreen]);

  const [pausedPhysics, setPausedPhysics] = useState(true);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPausedPhysics(false);
    }, 600);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="disable-selection">
      {isMobile ? (
        <p>
          This game is not compatible with mobile devices at the moment.
          <br />
          To enjoy the full gaming experience, please access this site from a
          desktop or laptop.
        </p>
      ) : (
        <>
          {user && (
            <Container className="canvas-container">
              <FullScreen handle={handle}>
                <Canvas
                  performance={{ min: 0.5 }}
                  shadows
                  className="canvas"
                  camera={{ fov: 65, near: 0.1, far: 1000 }}
                  onPointerDown={(e) => {
                    (e.target as HTMLCanvasElement).requestPointerLock();
                  }}
                >
                  {/* <Perf position="top-left" /> */}
                  <Lights />
                  <fog attach="fog" args={["#c1cabf", 15, 150]} />
                  <Physics timeStep="vary" paused={pausedPhysics}>
                    <KeyboardControls map={keyboardMap}>
                      <Ecctrl
                        debug
                        animated
                        followLight
                        springK={2}
                        dampingC={0.2}
                        autoBalanceSpringK={1.2}
                        autoBalanceDampingC={0.04}
                        onCastSpell={onCastSpell}
                      >
                        <Player />
                      </Ecctrl>
                    </KeyboardControls>
                    <EntityAI onCastSpell={onCastSpell} />
                    {scrollState !== ScrollStates.REMOVED && <Scroll />}
                    {pouchState !== PouchStates.REMOVED && <Pouch />}
                    <Trees boundary={100} count={100} />
                    <Ground />
                    <Environment files="/images/hdri/sky.hdr" background />
                    <Sky />
                    {spells.map((spell) => (
                      <Spell
                        key={(spell as any).id}
                        {...spell}
                        onHit={(position: any) =>
                          onHit((spell as any).id, position)
                        }
                      />
                    ))}
                    {hits.map((hit) => (
                      <SpellHit
                        key={hit.id}
                        {...hit}
                        onHitEnded={() => onHitEnded(hit.id)}
                      />
                    ))}
                  </Physics>
                </Canvas>
                <MemoryBar />
                <Minimap size={2} />
                {/* <StaminaBar /> */}
                {(gameState === GameStates.MENU ||
                  gameState === GameStates.WON ||
                  gameState === GameStates.GAME_OVER) && (
                  <Menu
                    handle={handle}
                    user={user}
                    onUserXpSaved={(user) => setUser(user)}
                  />
                )}
                {(gameState === GameStates.QUIZ ||
                  gameState === GameStates.SHOW_ANSWER) && <Quiz />}
                {showSettings && <Settings handle={handle} />}
                {(isLoading || userLoading) && (
                  <SplashScreen
                    isFullscreen={true}
                    externalProgress={progress}
                  />
                )}
              </FullScreen>
              {showUserLoadingError && <p>{t("error.refresh")}</p>}
            </Container>
          )}
        </>
      )}
    </div>
  );
};

export default GameCanvas;
